import type React from 'react';
import classes from '../employees-page.module.scss';
import { type SortField } from '../employees-page.types';
import { isManagerEmployeeRow, SORT_FIELD_LABELS } from '../employees-page.utils';
import { EmployeeAvatar } from '../../../components/EmployeeAvatar';
import { PhoneCell } from '../../../components/PhoneCell';
import type { useEmployeesPageController } from '../useEmployeesPageController';

type EmployeeDirectoryProps = Pick<
  ReturnType<typeof useEmployeesPageController>,
  | 'filteredAndSortedEmployees'
  | 'canCreateEmployees'
  | 'setIsCreateOpen'
  | 'onExport'
  | 'isExporting'
  | 'searchTerm'
  | 'setSearchTerm'
  | 'sortField'
  | 'onToggleSort'
  | 'sortDirection'
  | 'isManager'
  | 'canEditEmployees'
  | 'onOpenEditModal'
> & { hasNoEmployees: boolean; hasNoResults: boolean };
export const EmployeeDirectory: React.FC<EmployeeDirectoryProps> = ({
  filteredAndSortedEmployees,
  canCreateEmployees,
  setIsCreateOpen,
  onExport,
  isExporting,
  searchTerm,
  setSearchTerm,
  sortField,
  onToggleSort,
  sortDirection,
  isManager,
  canEditEmployees,
  onOpenEditModal,
  hasNoEmployees,
  hasNoResults,
}) => (
  <div className="workspaceLayout" dir="rtl">
    <aside className="contentCard workspaceSidebar employeesSidebar">
      <div className="workspaceSidebarHeader">
        <h1 className="homeTitle">עובדים</h1>
        <p className="homeHint">ניהול צוות העובדים</p>
      </div>

      <p className={classes.workspaceMetric}>
        <strong>{filteredAndSortedEmployees.length}</strong>
        <span>עובדים מוצגים</span>
      </p>

      {canCreateEmployees && (
        <button
          className={`button ${classes.employeesAddButton}`}
          type="button"
          onClick={() => setIsCreateOpen(true)}
        >
          הוספת עובד
        </button>
      )}
      <button
        className="button exportButton"
        type="button"
        onClick={() => {
          void onExport();
        }}
        disabled={isExporting}
      >
        {isExporting ? 'מייצא...' : 'ייצוא לאקסל'}
      </button>
    </aside>

    <section className={`contentCard workspaceMain ${classes.employeesCard}`}>
      <div className={classes.employeesHeaderRow}>
        <div>
          <h2 className="homeTitle">רשימת עובדים</h2>
          <p className="homeHint">חיפוש, מיון ועריכת פרטי עובדים</p>
        </div>
      </div>

      <div className={classes.employeesToolbar}>
        <input
          className={`input ${classes.employeesSearchInput}`}
          placeholder="חיפוש לפי שם פרטי או שם משפחה"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        <div className={classes.sortButtonsWrap}>
          {(Object.keys(SORT_FIELD_LABELS) as SortField[]).map((field) => (
            <button
              key={field}
              type="button"
              className={`${classes.sortButton}${sortField === field ? ' active' : ''}`}
              onClick={() => onToggleSort(field)}
            >
              {SORT_FIELD_LABELS[field]} {sortField === field ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
            </button>
          ))}
        </div>
      </div>

      {hasNoEmployees && (
        <div className={classes.emptyBox}>
          <p className="emptyStateText">עדיין אין עובדים להצגה.</p>
        </div>
      )}

      {!hasNoEmployees && hasNoResults && (
        <div className={classes.emptyBox}>
          <p className="emptyStateText">לא נמצאו עובדים עבור החיפוש שבוצע.</p>
        </div>
      )}

      {!hasNoEmployees && !hasNoResults && (
        <div className={classes.employeesTableWrap}>
          <table role="table" className={`mobileRecordTable ${classes.employeesTable} ${classes.employeesDirectoryTable}`}>
            <thead>
              <tr>
                <th>עובד</th>
                <th>טלפון</th>
                {isManager && <th>מחלקה</th>}
                {isManager && <th>פעולות</th>}
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedEmployees.map((employee, index) => {
                const rowKey =
                  isManager && isManagerEmployeeRow(employee)
                    ? `manager-${employee.employeePk}`
                    : `worker-${employee.firstName}-${employee.lastName}-${index}`;

                return (
                  <tr key={rowKey}>
                    <td data-label="עובד">
                      <div className={classes.employeeIdentityCell}>
                        <EmployeeAvatar
                          firstName={employee.firstName}
                          lastName={employee.lastName}
                          photoUrl={employee.photoUrl}
                        />
                        <span className={classes.employeeDisplayName}>
                          {employee.firstName} {employee.lastName}
                        </span>
                      </div>
                    </td>
                    <td data-label="טלפון">
                      <PhoneCell value={employee.phoneNumber} />
                    </td>
                    {isManager && isManagerEmployeeRow(employee) ? (
                      <>
                        <td data-label="מחלקה">{employee.sectionName ?? '—'}</td>
                        <td data-label="פעולות">
                          <div className={classes.employeeRowActions}>
                            {canEditEmployees && (
                              <button
                                type="button"
                                className="navArrowButton"
                                onClick={() => {
                                  void onOpenEditModal(employee.employeePk);
                                }}
                              >
                                עריכה
                              </button>
                            )}
                          </div>
                        </td>
                      </>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  </div>
);
